import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { AuthService } from 'src/app/modules/auth/shared/auth.service';

@Injectable({
  providedIn: 'root',
})
export class BetService {
  raceDetailsForm: FormGroup;
  minStake = 0.5;
  selectedStake = this.minStake;
  selectedStakeChangeEvent$ = new Subject();
  formValidationEvent$ = new Subject();
  showConfirmBetPopupEvent$ = new Subject();
  totalPrice: number;
  numberofBets: number;
  placeBetSpinner: boolean;

  allAddedBetsInCart$ = new BehaviorSubject([]);
  showCurrentBet$ = new BehaviorSubject(true);
  totalAllBetsOnCart = 0;

  subscription: Subscription;

  constructor(private formBuilder: FormBuilder, private authService: AuthService) {
    this.subscription = this.authService.currentCountry$.subscribe((country) => {
      if (country) {
        this.selectedStake = country.minStakes;
        this.minStake = country.minStakes;
      }
    });
  }

  resetSelectedState() {
    this.selectedStake = this.minStake;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  stakeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const hasErr = control.value >= this.minStake ? false : true;
      if (hasErr) {
        return { stakeVal: true };
      }
      return null;
    };
  }


  initRaceDetailsForm(): void {
    this.raceDetailsForm = this.formBuilder.group({
      selectedStake: [
        this.selectedStake,
        Validators.compose([Validators.required, this.stakeValidator()]),
      ],
    });
    this.raceDetailsForm.get('selectedStake').valueChanges.subscribe((val) => {
      this.selectedStakeChangeEvent$.next(val);
    });
    this.raceDetailsForm.get('selectedStake').statusChanges.subscribe((val) => {
      this.formValidationEvent$.next(val === 'VALID' ? true : false);
    });
  }

  // The step follows the country's own minimum stake rather than a hardcoded 1.
  // Tunisia's minimum is 0.5 with quick-stake chips of 0.5/1/1.5/..., so a step
  // of 1 walked straight past every chip value (0.5 -> 1.5 -> 2.5), and the old
  // `val < 1 ? 1 : val` floor meant pressing minus at the 0.5 minimum RAISED the
  // stake to 1. Rounding to 2 decimals keeps repeated steps off float dust
  // (0.1+0.2 style drift) so the value stays exactly what the customer sees.
  private stepAmount(): number {
    const step = Number(this.minStake);
    return Number.isFinite(step) && step > 0 ? step : 1;
  }

  private roundStake(value: number): number {
    return Math.round(value * 100) / 100;
  }

  increase(): void {
    const current = Number(this.selectedStake) || 0;
    this.raceDetailsForm.controls.selectedStake.patchValue(
      this.roundStake(current + this.stepAmount())
    );
  }

  decrease(): void {
    const min = this.stepAmount();
    const current = Number(this.selectedStake) || 0;
    // Already at or under the minimum: do nothing. Clamping up to `min` here
    // would make the MINUS button increase the stake — the very bug this
    // replaced, just at a different starting value.
    if (current <= min) {
      return;
    }
    const next = this.roundStake(current - min);
    this.raceDetailsForm.controls.selectedStake.patchValue(
      next < min ? min : next
    );
  }
}
